import { Module, MutationTree, ActionTree, GetterTree } from "vuex"
import { RootState } from "@/store"
import { DeviceType, RefetchDataType } from "@/types/setup"
import axios from "axios"

export interface WizardState {
    currentStep: number
    isLoading: boolean
    showWizard: boolean
    refetchParent: RefetchDataType | null
}

const state: WizardState = {
    currentStep: 1,
    isLoading: false,
    showWizard: false,
    refetchParent: null,
}

const mutations: MutationTree<WizardState> = {
    setCurrentStep(state, step: number) {
        state.currentStep = step
    },
    setLoading(state, isLoading: boolean) {
        state.isLoading = isLoading
    },
    setShowWizard(state, showWizard: boolean) {
        state.showWizard = showWizard
    },
    setRefetchParent(state, type: RefetchDataType) {
        state.refetchParent = type
    },
}

const actions: ActionTree<WizardState, RootState> = {
    startWizard({ dispatch, rootState }, deviceType: DeviceType) {
        const locationId = rootState.setup.location.existingLocation

        dispatch("setup/device/setDeviceType", deviceType, { root: true })
        dispatch("setup/user/setUser", null, { root: true })

        if (locationId) {
            dispatch("nextStep")
        }
    },
    nextStep({ commit, state }) {
        if (state.currentStep < 4) {
            commit("setCurrentStep", state.currentStep + 1)
        }
    },
    prevStep({ commit, state }) {
        if (state.currentStep > 1) {
            commit("setCurrentStep", state.currentStep - 1)
        }
    },
    async completeSetup({ commit, rootGetters, dispatch }, { onSuccess }) {
        commit("setLoading", true)
        try {
            const existingLocation = rootGetters["setup/location/existingLocation"]
            const companyId = rootGetters["setup/location/companyId"]
            const locationGroupId = rootGetters["setup/location/locationGroupId"]
            const location = rootGetters["setup/location/newLocation"]
            const controller = rootGetters["setup/device/currentController"]
            const supplierEnergyBuy = rootGetters["setup/tariff/supplierEnergyBuy"] || null
            const supplierEnergySell = rootGetters["setup/tariff/supplierEnergySell"] || null
            const distributorEnergy = rootGetters["setup/tariff/distributorEnergy"] || null
            const distributorPower = rootGetters["setup/tariff/distributorPower"] || null

            const setupData = {
                data: {
                    location: {
                        name: location.name,
                        countryCode: location.country?.code,
                        region: {
                            code: location.region?.code,
                            type: location.region?.type,
                        },
                        timezone: location.timezone?.name,
                        coordinates: {
                            latitude: location.coordinates?.latitude ?? null,
                            longitude: location.coordinates?.longitude ?? null,
                        },
                    },
                    node: {
                        unitId: controller?.unitId,
                    },
                    controller: {
                        unitId: controller?.unitId,
                    },
                    userId: null,
                    companyId: companyId,
                    locationGroupId: locationGroupId,
                    configs: {
                        supplierEnergyBuyConfigId: supplierEnergyBuy?.id,
                        supplierEnergySellConfigId: supplierEnergySell?.id,
                        distributorEnergyConfigId: distributorEnergy?.id,
                        distributorPowerConfigId: distributorPower?.id,
                    },
                },
            }

            const url =
                existingLocation?.id && companyId
                    ? `/companies/${companyId}/locations/${existingLocation.id}/controllers/assign`
                    : "/v2/locations/setup"

            if (!existingLocation) {
                setupData.data.userId = location.ownerId
            }

            const refetchType =
                existingLocation?.id && companyId
                    ? RefetchDataType.CONTROLLERS
                    : RefetchDataType.LOCATIONS

            const response = await axios.post(url, setupData)

            dispatch(
                "toastMessageModule/showMessage",
                {
                    message: "setup.setup_success",
                    color: "green",
                    delay: 5000,
                    isTranslation: true,
                },
                { root: true },
            )
            dispatch("refetchParent", refetchType)
            await dispatch("auth/fetch", null, { root: true })
            await dispatch("cleanup")

            onSuccess?.()

            return response.data
        } catch (error) {
            dispatch(
                "toastMessageModule/showMessage",
                {
                    message: "setup.setup_failed",
                    color: "red",
                    delay: 5000,
                    isTranslation: true,
                },
                { root: true },
            )
            throw error
        } finally {
            commit("setLoading", false)
        }
    },
    cleanup({ commit, dispatch }) {
        commit("setCurrentStep", 1)
        dispatch("setup/location/resetLocation", null, { root: true })
        dispatch("setup/device/setDeviceType", "", { root: true })
        dispatch("setup/device/resetUnit", null, { root: true })
        dispatch("setup/tariff/resetTariff", null, { root: true })
        dispatch(
            "setup/device/completeControllerStep",
            { controller: null, devices: [] },
            { root: true },
        )
        dispatch("setup/location/setExistingLocation", null, { root: true })
        commit("setShowWizard", false)
        commit("setRefetchParent", null)
    },
    refetchParent({ commit }, type: RefetchDataType) {
        commit("setRefetchParent", type)
    },
}

const getters: GetterTree<WizardState, RootState> = {
    currentStep: (state) => state.currentStep,
    isLoading: (state) => state.isLoading,
    progressBarWidth: (state) => {
        const currentStep = state.currentStep
        switch (currentStep) {
            case 1:
                return "12.5%"
            case 2:
                return "37.5%"
            case 3:
                return "62.5%"
            case 4:
                return "87.5%"
            default:
                return "0%"
        }
    },
    isCurrentStep: (state) => (step: number) => step === state.currentStep,
    isStepDone: (state) => (step: number) => state.currentStep > step,
    canContinueToNextStep: (_, __, rootState, rootGetters) => {
        switch (rootState.setup.wizard.currentStep) {
            case 1:
                return rootGetters["setup/location/canContinue"]
            case 2:
                return rootGetters["setup/device/canContinue"]
            case 3:
                return true
            case 4:
                return true
            default:
                return false
        }
    },
    showWizard: (state) => state.showWizard,
    refetchParent: (state) => state.refetchParent,
}

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
} as Module<WizardState, RootState>
